import { useParams, Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Code2 } from "lucide-react";
import { useGetProject } from "@workspace/api-client";
import { useQuery } from "@tanstack/react-query";
import { ImageSlider } from "@/components/common/ImageSlider";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";

export default function WorkDetail() {
  const { slug } = useParams();
  const { data: project, isLoading } = useGetProject(slug || "", { query: { enabled: !!slug } } as any);
  const { data: media } = useQuery<any[]>({
    queryKey: ["media", "PROJECT", project?.id],
    queryFn: async () => {
      const res = await fetch(`/api/media/PROJECT/${project?.id}`);
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json();
    },
    enabled: !!project?.id,
  });

  if (isLoading) {
    return <div className="pt-32 pb-20 px-4 text-center text-muted-foreground">Loading...</div>;
  }

  if (!project) {
    return (
      <div className="pt-32 pb-20 px-4 text-center">
        <h1 className="font-display text-4xl font-bold mb-4">Project Not Found</h1>
        <p className="text-muted-foreground mb-8">This case study doesn't exist or has been removed.</p>
        <Link href="/work" className="inline-flex items-center text-primary hover:underline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Portfolio
        </Link>
      </div>
    );
  }

  const techStack = project.techStack ? project.techStack.split(',') : [];

  return (
    <div className="flex flex-col w-full overflow-x-hidden">
      <SEO
        title={`${project.title} | Case Study`}
        description={project.description}
        image={project.coverImage}
        type="article"
      />
      <JsonLd schema={[
        {
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          "name": project.title,
          "url": `https://acceleratebyte.com/work/${slug}`,
          "description": project.description,
          "image": project.coverImage || "https://acceleratebyte.com/opengraph.jpg",
          "creator": { "@id": "https://acceleratebyte.com/#organization" },
          ...(project.longDescription ? { "about": project.longDescription.replace(/<[^>]+>/g, '').substring(0, 300) } : {}),
          ...(techStack.length > 0 ? { "keywords": techStack.join(', ') } : {}),
          ...(project.liveUrl ? { "sameAs": project.liveUrl } : {})
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://acceleratebyte.com" },
            { "@type": "ListItem", "position": 2, "name": "Work", "item": "https://acceleratebyte.com/work" },
            { "@type": "ListItem", "position": 3, "name": project.title, "item": `https://acceleratebyte.com/work/${slug}` }
          ]
        }
      ]} />
      <section className="pt-32 pb-16 px-4 border-b border-border/50 bg-muted/10">
        <div className="container mx-auto max-w-5xl">
          <Link href="/work" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Portfolio
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {project.category && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-4">
                  {project.category}
                </motion.div>
              )}
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {project.title}
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-muted-foreground">
                {project.description}
              </motion.p>
            </div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="bg-card border border-border/50 rounded-2xl p-6">
              <h2 className="font-bold text-lg mb-4 border-b border-border/50 pb-2">Project Details</h2>
              <div className="space-y-4 text-sm">
                {project.clientName && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Client</span>
                    <span className="font-medium">{project.clientName}</span>
                  </div>
                )}
                {techStack.length > 0 && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Tech Stack</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {techStack.map((tech: string, i: number) => (
                        <span key={i} className="bg-muted px-2 py-1 rounded-md text-xs font-medium">{tech.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {(project.liveUrl || project.repoUrl) && (
                  <div className="pt-4 flex gap-4">
                    {project.liveUrl && (
                      <a href={project.liveUrl} target="_blank" rel="noreferrer" className="inline-flex items-center text-primary hover:underline">
                        Live Site <ExternalLink className="ml-1 w-3 h-3" />
                      </a>
                    )}
                    {project.repoUrl && (
                      <a href={project.repoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center text-primary hover:underline">
                        Repository <Code2 className="ml-1 w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {media && media.length > 0 && (
        <section className="py-12 px-4 container mx-auto max-w-5xl">
          <ImageSlider images={media} />
        </section>
      )}

      <section className="py-20 px-4 container mx-auto max-w-4xl overflow-x-hidden">
        {project.longDescription && (
          <div className="prose prose-invert prose-lg max-w-none break-words [&_*]:max-w-full">
            <h2 className="font-display">The Challenge & Solution</h2>
            <div dangerouslySetInnerHTML={{ __html: project.longDescription }} />
          </div>
        )}

        {project.result && (
          <div className="prose prose-invert prose-lg max-w-none break-words [&_*]:max-w-full">
            <h2 className="font-display">The Results</h2>
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 my-8 not-prose">
              <p className="text-lg font-medium text-primary-foreground leading-relaxed">
                "{project.result}"
              </p>
            </div>
          </div>
        )}

        <div className="mt-20 pt-10 border-t border-border/50 text-center">
          <h3 className="font-display text-3xl font-bold mb-6">Need similar results?</h3>
          <Link href="/contact" className="inline-flex h-12 items-center justify-center rounded-md bg-gradient-to-r from-blue-600 to-purple-600 px-8 text-base font-medium text-white shadow transition-transform hover:scale-105">
            Discuss Your Project
          </Link>
        </div>
      </section>
    </div>
  );
}
